const statusOf = (error: unknown): unknown =>
  error && typeof error === 'object' ? (error as { status?: unknown }).status : undefined

/**
 * Did loading the entity fail because the user may not see it? (GH #1731)
 *
 * Access denied:
 * - `403` — the API's denials are JSON (`HttpError` bodies, API Gateway's default responses).
 * - `401` while the session is valid. The entity read still answers denials with 401 (#1907), and
 *   `baseQueryWithReauth` treats a 401 with a valid session as "no access". It arrives in two
 *   shapes: nested under `.error` when that request ends it with `retry.fail`
 *   (reduxjs/redux-toolkit#3789), and bare when a concurrent request held the re-auth mutex and this
 *   one simply retried — opening a forbidden entity fires several such requests at once.
 *
 * Not access denied:
 * - A bare 401 without a valid session: re-authentication is the answer there, not a permission
 *   message (a transient refresh failure surfaces exactly this).
 * - A 403 whose body is not JSON (`PARSING_ERROR`, `originalStatus: 403`): that body is generated
 *   at the edge, not by the API. Every such event in TUNASONG-W9 is CloudFront's WAF
 *   "Request blocked" page — a rate or reputation block that a later retry can get past. Telling
 *   that user they lack access, with no retry, would be false.
 */
export const isEntityAccessDenied = (error: unknown, hasValidSession: boolean): boolean => {
  const status = statusOf(error)
  if (status === 403 || (status === 401 && hasValidSession)) {
    return true
  }
  const inner = error && typeof error === 'object' ? (error as { error?: unknown }).error : undefined
  const innerStatus = statusOf(inner)
  return innerStatus === 401 || innerStatus === 403
}
